import { useState } from 'react'
import { MAX_SERIES, SERIES } from '../charts/chartColors.js'
import type { PortfolioRow } from '../types.js'

/** Up to four facilities drawn as lines of their own, each keeping its colour slot until it is taken off. */
export type ChosenSlots = readonly (string | null)[]

export const EMPTY_SLOTS: ChosenSlots = [null, null, null, null]

export function slotsFrom(rows: readonly PortfolioRow[], count = 3): ChosenSlots {
  return EMPTY_SLOTS.map((_, index) => (index < count ? (rows[index]?.facilityId ?? null) : null))
}

/** The slots with any facility no longer in the portfolio taken off. */
export function validSlots(slots: ChosenSlots, rows: readonly PortfolioRow[]): ChosenSlots {
  const present = new Set(rows.map((row) => row.facilityId))
  return slots.map((id) => (id !== null && present.has(id) ? id : null))
}

export function colorOfSlot(slots: ChosenSlots, facilityId: string): string | null {
  const index = slots.indexOf(facilityId)
  return index === -1 ? null : (SERIES[index] ?? null)
}

export function chosenRowsOf(slots: ChosenSlots, rows: readonly PortfolioRow[]): { row: PortfolioRow; color: string; slot: number }[] {
  return slots.flatMap((id, slot) => {
    const row = id === null ? undefined : rows.find((entry) => entry.facilityId === id)
    return row ? [{ row, color: SERIES[slot] ?? '#0A2830', slot }] : []
  })
}

const SHOWN = 12

/**
 * The chips a facility is chosen by: the chosen ones first, then the next of the list, and a picker for the rest
 * where a client has more facilities than fit in a row. A chosen facility keeps its colour until it is taken off.
 */
export function FacilityChoice({ rows, slots, onChange }: { rows: readonly PortfolioRow[]; slots: ChosenSlots; onChange: (slots: ChosenSlots) => void }) {
  const [showAll, setShowAll] = useState(false)
  const chosen = new Set(slots.filter((id): id is string => id !== null))
  const full = chosen.size >= MAX_SERIES
  const toggle = (facilityId: string): void => {
    if (chosen.has(facilityId)) {
      onChange(slots.map((id) => (id === facilityId ? null : id)))
      return
    }
    const free = slots.indexOf(null)
    if (free !== -1) {
      onChange(slots.map((id, index) => (index === free ? facilityId : id)))
    }
  }
  const ordered = [...rows].sort((a, b) => Number(chosen.has(b.facilityId)) - Number(chosen.has(a.facilityId)) || a.name.localeCompare(b.name))
  const shown = showAll ? ordered : ordered.slice(0, SHOWN)
  const hidden = ordered.slice(SHOWN)
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Facilities drawn">
      {shown.map((row) => {
        const on = chosen.has(row.facilityId)
        const color = colorOfSlot(slots, row.facilityId)
        return (
          <button key={row.facilityId} type="button" aria-pressed={on} onClick={() => toggle(row.facilityId)} disabled={!on && full} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold ${on ? 'border-transparent text-white' : 'border-marine/30 bg-white text-ink-mid disabled:opacity-40'}`} style={on && color ? { background: color } : undefined}>
            {row.name}
          </button>
        )
      })}
      {!showAll && hidden.length > 0 && (
        <select aria-label="Add a facility" value="" disabled={full} onChange={(event) => event.target.value && toggle(event.target.value)} className="rounded-full border border-marine/30 bg-white px-2 py-1 text-[11px] text-ink-mid disabled:opacity-40">
          <option value="">{hidden.length} more…</option>
          {hidden.map((row) => (
            <option key={row.facilityId} value={row.facilityId}>
              {row.name}
            </option>
          ))}
        </select>
      )}
      {hidden.length > 0 && (
        <button type="button" onClick={() => setShowAll((current) => !current)} className="text-[11px] font-bold text-marine hover:text-marine-dk">
          {showAll ? 'Fewer' : 'Show all'}
        </button>
      )}
      <span className="self-center text-[10px] text-ink-mid">Up to {MAX_SERIES} at a time</span>
    </div>
  )
}
